/**
 * Le dice a la pagina que nos empotra cuanto alto ocupamos, para que ajuste el iframe.
 *
 * Un `<iframe>` no se adapta solo: tiene la altura que le pongan y punto. Y este
 * formulario cambia de alto todo el rato — aparece la recomendacion al escribir el
 * consumo, aparecen errores debajo de un campo, y al enviarlo se convierte en la
 * pantalla de gracias, mucho mas corta.
 *
 * Con una altura fija solo hay dos finales, los dos malos: una barra de desplazamiento
 * dentro del recuadro —que en movil es horrible— o un hueco en blanco enorme debajo del
 * formulario.
 *
 * La solucion estandar: aqui medimos y avisamos, y la pagina de WordPress escucha y
 * redimensiona. Ver la seccion "Empotrarlo en WordPress" del README.
 */

interface MensajeDeAltura {
  tipo: 'cotizador:altura'
  alto: number
}

/**
 * Empieza a informar de la altura. No hace nada si la pagina no esta empotrada.
 *
 * El mensaje viaja con destino `'*'` (cualquier padre) y es deliberado: lo unico que
 * lleva es un numero de pixeles. Restringirlo al dominio de SunSmart obligaria a que el
 * formulario supiera desde donde lo empotran —un dato que vive en el servidor— y a
 * pasarlo hasta aqui, todo para proteger un dato que no es secreto.
 *
 * Quien NO puede empotrarnos ya lo decide el servidor con la cabecera `frame-ancestors`,
 * que es donde se defiende eso de verdad.
 */
export function informarDeLaAltura(): void {
  if (window.parent === window) return

  // Dentro de la web de SunSmart la marca ya esta arriba de la pagina: el logo y el pie
  // del formulario la repetirian dos veces en la misma pantalla. Fuera de un iframe
  // —abriendo la URL directamente— siguen apareciendo, porque ahi si hacen falta.
  document.documentElement.classList.add('empotrado')

  /*
   * Se mide EL CONTENIDO, no el documento, y esa distincion es todo el asunto.
   *
   * La primera version usaba `document.documentElement.scrollHeight`, que fuera de un
   * iframe da el alto del contenido. Dentro de uno, no: nunca baja del alto de la
   * ventana, y la ventana es el propio iframe. O sea que el formulario medía su marco,
   * respondía "mido exactamente lo que ya mides" y el recuadro no se ajustaba jamas.
   *
   * Peor todavia: asi el alto solo podia CRECER. Al pasar a la pantalla de gracias, que
   * es mucho mas corta, quedaba medio iframe en blanco para siempre.
   *
   * `.pagina` es nuestro contenedor y su alto incluye su relleno. Ese es el numero real.
   */
  const contenido = document.querySelector<HTMLElement>('.pagina')
  const medir = () =>
    contenido ? Math.ceil(contenido.getBoundingClientRect().height) : document.body.scrollHeight

  let ultimo = 0

  const avisar = () => {
    const alto = medir()

    // Sin este umbral, un pixel de diferencia por el redondeo del navegador dispara un
    // mensaje, que cambia la altura, que dispara otro mensaje: el iframe tiembla solo.
    if (Math.abs(alto - ultimo) < 2) return
    ultimo = alto

    const mensaje: MensajeDeAltura = { tipo: 'cotizador:altura', alto }
    window.parent.postMessage(mensaje, '*')
  }

  // `ResizeObserver` y no un temporizador: avisa cuando de verdad cambia algo, en vez de
  // preguntar diez veces por segundo para que la respuesta sea "nada" nueve de ellas.
  new ResizeObserver(avisar).observe(contenido ?? document.body)

  // El primero, en cuanto haya pintado: al observar todavia no hay nada medible.
  requestAnimationFrame(avisar)
}
